import React, {
  useEffect,
  useState,
  createContext,
} from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { isEmpty, upperFirst, camelCase } from 'lodash';

import { useQueryParam, authorizedPyInstance as py } from '~/utils';
import usePatient from '~/utils/patient/hook';
import PrintHeader from '~/pages/patients/identity/printHeader';
import PrintButton from '~/pages/patients/identity/printButton';
import PatientsBannerModal from '~/components/Patient/Identity/Banner/Modal';

const IdentityPageContext = createContext({});

const IdentityPageProvider = ({
  pageSpec,
  children,
}) => {
  const [, patient, actions] = usePatient();
  const [accordianItems, setAccordianItems] = useState([]);
  const [allPanelsOpen, setAllPanelsOpen] = useState(false);
  const [openPanel, setOpenPanel] = useQueryParam('expand');

  const [printSpec, setPrintSpec] = useState({});
  const [isPrintingStaged, setIsPrintingStaged] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printAllSpec, setPrintAllSpec] = useState({});

  const expandPanel = panel => {
    setIsPrintingStaged(false);
    setOpenPanel(panel);
  };

  const print = () => {
    const { ...spec } = printSpec;
    setIsPrinting(true);

    py
      .post(`/api/data/public/identity/patient/${patient.id}/pdf`, spec)
      .then(r => {
        window.open(r.data.data.link, '_blank');
      })
      .catch(() => {
        toast.error('Failed to build PDF.');
      })
      .finally(() => {
        setIsPrinting(false);
      });
  };

  const resetPrint = () => {
    setPrintSpec([]);
    setAllPanelsOpen(false);
    setIsPrintingStaged(false);
  };

  const reSpecPrintAll = (allSpec, itemPrintSpec) => {
    const newAllSpec = { ...allSpec };

    const specItemTitle = Object.keys(itemPrintSpec)[0];
    if (specItemTitle in newAllSpec) {
      newAllSpec[specItemTitle].pageItems = [
        ...newAllSpec[specItemTitle].pageItems,
        ...itemPrintSpec[specItemTitle].pageItems,
      ];
    } else {
      newAllSpec[specItemTitle] = { pageItems: itemPrintSpec[specItemTitle].pageItems };
    }

    return newAllSpec;
  };

  const bootstrapPrintForItem = item => {
    const accordianItem = { ...item };

    const itemPrintSpec = {
      [pageSpec.title]: {
        pageItems: [{
          title: item.title,
        }],
      },
    };

    const setupPrint = () => {
      setPrintSpec(itemPrintSpec);
      setIsPrintingStaged(true);
    };

    accordianItem.titleAction = (
      pageSpec.enableIndividualPrint !== false ?
        (<PrintButton
          onClick={setupPrint}
        />) : null
    );

    return [accordianItem, itemPrintSpec];
  };

  // Prepare the page via consuming the pageSpec
  useEffect(() => {
    if (!isEmpty(pageSpec)) {
      // build out the accordian items and setup print when applicable
      const auditTitle = upperFirst(camelCase(pageSpec.title));
      let tmpPrintAllSpec = {};
      setAccordianItems(pageSpec.panels.map(panel => {
        if (panel.enablePrint !== false) {
          const [accordianItem, itemPrintSpec] = bootstrapPrintForItem(panel);

          tmpPrintAllSpec = reSpecPrintAll(tmpPrintAllSpec, itemPrintSpec);

          return accordianItem;
        }

        return panel;
      }));

      setPrintAllSpec(tmpPrintAllSpec);

      // Audit page view
      actions.audit(auditTitle);
    }
  }, [pageSpec]);

  // Create a dummy function for modal toggle
  const toggleModal = () => {
    // This won't actually close the modal when patient is out of custody
    // since we've set backdrop="static" and keyboard={false} in the modal
  };

  // If patient is out of custody, render our custom banner modal over an empty page
  if (patient && patient.inCustody === false) {
    return (
      <>
        <PatientsBannerModal
          isOpen
          toggleIsOpen={toggleModal}
          patient={patient}
          onCancel={() => {
            window.history.back();
          }}
        />
        <div className="content" />
      </>
    );
  }

  return (
    <>
      <IdentityPageContext.Provider
        value={{
          accordianItems,
          allPanelsOpen,
          expandPanel,
          openPanel,
          printSpec,
          setPrintSpec,
          isPrintingStaged,
          setIsPrintingStaged,
          isPrinting,
          print,
          resetPrint,
        }}
      >
        {!isPrintingStaged
          ? (
            <>
              <div className="d-flex mb-3 border-bottom align-items-center">
                <h1>{pageSpec.title}</h1>

                {pageSpec.enableFullPrint !== false
                  ? (
                    <PrintButton
                      onClick={() => {
                        setPrintSpec(printAllSpec);
                        setAllPanelsOpen(true);
                        setIsPrintingStaged(true);
                      }}
                    />
                  ) : null}
              </div>
            </>
          ) : (
            <>
              <div className="page-header">
                <PrintHeader
                  pageTitle={pageSpec.title}
                />
              </div>
            </>
          )}
        <div className="content">
          {children({
              openPanel,
              accordianItems,
              allPanelsOpen,
              expandPanel,
            })}
        </div>
      </IdentityPageContext.Provider>
    </>
  );
};

IdentityPageProvider.propTypes = {
  children: PropTypes.node.isRequired,
  pageSpec: PropTypes.shape().isRequired,
};

export {
  IdentityPageContext,
  IdentityPageProvider,
};
